import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import { DESIGN_PRESETS } from "../Context/DesignPresets";
import { getDesignLabel } from "../Context/DesignLabels";
import { useAuth } from "../Context/AuthContext";
import "../CSS/CatalogPage.css";

// ── Helpers ─────────────────────────────────────────────────────────────────

const FILTERS = ["All", "Abstract", "Floral", "Klasik", "K-Drama"];

const CATEGORY_MAP = {
    modern: "Floral",
    classic: "Klasik",
};

const CATEGORY_ACCENT = {
    All: "#b56276",
    Floral: "#b56276",
    Klasik: "#613813",
    Abstract: "#D16BA5",
};

// All designs as a flat array with their category attached
const ALL_DESIGNS = Object.entries(DESIGN_PRESETS).map(([code, preset]) => {
    let category = CATEGORY_MAP[preset.designTemplate] ?? "Other";

    // Abstract and Floral both share "modern" template.
    // Differentiate them by the design code prefix.
    if (code.startsWith("abstract")) {
        category = "Abstract";
    }
    if (code.startsWith("kdrama")) {
        category = "K-Drama"
    }

    return {
        code,
        category,
        ...preset,
    };
});

// ── DesignCard ────────────────────────────────────────────────────────────────

const DesignCard = ({ code, cardBackground, themeColour, themeBaseCardColour, onOrder, actionText = "Order" }) => {
    const [hovered, setHovered] = useState(false);
    const label = getDesignLabel(code);
    const handlePreview = () => { window.open(`/design-preview/${code}`, '_blank', 'noopener,noreferrer') };
    const handleOrder = (event) => {
        event.stopPropagation();
        onOrder(code);
    };

    return (
        <div
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
                borderRadius: "18px",
                overflow: "hidden",
                background: "#fff",
                boxShadow: hovered
                    ? "0 16px 40px rgba(0,0,0,0.22)"
                    : "0 4px 16px rgba(0,0,0,0.10)",
                transform: hovered ? "translateY(-6px) scale(1.02)" : "translateY(0) scale(1)",
                transition: "all 0.28s cubic-bezier(0.34, 1.56, 0.64, 1)",
                cursor: "pointer",
                display: "flex",
                flexDirection: "column",
            }}
            onClick={handlePreview}
        >
            {/* Thumbnail */}
            <div
                style={{
                    width: "100%",
                    aspectRatio: "3/4.3",
                    overflow: "hidden",
                    position: "relative",
                    background: "#ffffffff",
                }}
            >
                {cardBackground &&
                    <img
                        src={cardBackground}
                        alt={label}
                        style={{
                            width: "100%",
                            height: "100%",
                            backgroundSize: "cover",
                            backgroundSize: "100% 100%",
                            transform: hovered ? "scale(1.07)" : "scale(1)",
                            transition: "transform 0.4s ease",
                        }}
                    />
                }

                {/* Overlay on hover — desktop only */}
                <div
                    className="card-hover-overlay"
                    style={{
                        position: "absolute",
                        inset: 0,
                        background: hovered ? "rgba(0,0,0,0.28)" : "rgba(0,0,0,0)",
                        transition: "background 0.28s ease",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "column",
                        gap: "10px",
                    }}
                >
                    {hovered && (
                        <>
                            <button
                                type="button"
                                className="catalog-card-action-btn"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    handlePreview();
                                }}
                            >
                                Preview
                            </button>
                            <button
                                type="button"
                                className="catalog-card-action-btn catalog-card-action-btn--order"
                                onClick={handleOrder}
                            >
                                {actionText}
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* Footer */}
            <div
                style={{
                    padding: "10px 12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                }}
            >
                {/* Colour dot */}
                <div
                    style={{
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                        background: themeColour ?? "#ccc",
                        flexShrink: 0,
                        boxShadow: `0 0 0 2px ${(themeColour ?? "#cccccc").slice(0, 7)}44`,
                    }}
                />
                <span
                    style={{
                        fontFamily: "Poppins, sans-serif",

                        fontSize: "12px",
                        color: "#333",
                        fontWeight: 500,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    }}
                >
                    {label}
                </span>
            </div>

            {/* Mobile-only button — shown below card on small screens */}
            <div className="card-mobile-btn" style={{ padding: "0px 10px 15px" }}>
                <button
                    type="button"
                    className="catalog-card-action-btn catalog-card-action-btn--mobile"
                    onClick={(event) => {
                        event.stopPropagation();
                        handlePreview();
                    }}
                >
                    Preview
                </button>
                <button
                    type="button"
                    className="catalog-card-action-btn catalog-card-action-btn--order catalog-card-action-btn--mobile"
                    onClick={handleOrder}
                >
                    {actionText}
                </button>
            </div>
        </div>
    );
};

// ── Main Page ────────────────────────────────────────────────────────────────

const CatalogPage = () => {
    const [activeFilter, setActiveFilter] = useState("All");
    const [loginModalOpen, setLoginModalOpen] = useState(false);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const editId = searchParams.get('id');
    const slug = searchParams.get('slug');
    const { isLoggedIn } = useAuth();

    const visibleDesigns =
        activeFilter === "All"
            ? ALL_DESIGNS
            : ALL_DESIGNS.filter((d) => d.category === activeFilter);

    const handleOrder = (code) => {
        if (!isLoggedIn) {
            setLoginModalOpen(true);
            return;
        }

        if (editId && slug) {
            navigate(`/studio?id=${editId}&slug=${slug}&design=${encodeURIComponent(code)}`);
        } else {
            navigate(`/studio?design=${encodeURIComponent(code)}`);
        }
    };

    return (
        <div
            style={{
                minHeight: "100vh",
                background: "white",
                paddingBottom: "60px",
            }}
        >

            {/* ── Filter Tab Bar ── */}
            <div
                style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "10px",
                    padding: "32px 20px 24px",
                    flexWrap: "wrap",
                }}
            >
                {FILTERS.map((filter) => {
                    const isActive = activeFilter === filter;
                    const filterAccent = CATEGORY_ACCENT[filter] ?? "#b56276";
                    return (
                        <button
                            key={filter}
                            className="filter-btn"
                            onClick={() => setActiveFilter(filter)}
                            style={{
                                fontFamily: "Poppins, sans-serif",
                                fontSize: "13px",
                                fontWeight: isActive ? 600 : 400,
                                padding: "8px 25px",
                                borderRadius: "999px",
                                border: isActive ? "none" : "1px solid rgba(255,255,255,0.18)",
                                backgroundColor: isActive
                                    ? "#55C2C3"
                                    : "#ffffff",
                                color: isActive ? "white" : "#333333ff",
                                cursor: "pointer",
                                transition: "all 0.22s ease",
                                boxShadow: "0px 4px 14px rgba(0, 0, 0, 0.12)",
                                letterSpacing: "0.3px",
                            }}
                        >
                            {filter}
                        </button>
                    );
                })}
            </div>

            {/* ── Count badge ── */}
            <div style={{ textAlign: "center", marginBottom: "20px" }}>
                <span
                    style={{
                        fontFamily: "Poppins, sans-serif",
                        fontSize: "12px",
                        color: "rgba(32, 32, 32, 0.3)",
                        letterSpacing: "0.5px",
                    }}
                >
                    {visibleDesigns.length} designs
                </span>
            </div>

            {/* ── Grid ── */}
            <div
                className="catalog-grid"
                style={{
                    maxWidth: "1000px",
                    margin: "0 auto",
                    padding: "0 20px",
                    gap: "25px",
                }}
            >
                {visibleDesigns.map(({ code, ...preset }) => (
                    <DesignCard key={code} code={code} {...preset} onOrder={handleOrder} actionText={editId ? "Change" : "Order"} />
                ))}
            </div>

            <Dialog
                open={loginModalOpen}
                onClose={() => setLoginModalOpen(false)}
                PaperProps={{
                    sx: {
                        borderRadius: "18px",
                        width: "calc(100% - 32px)",
                        maxWidth: "420px",
                        p: 1,
                    },
                }}
            >
                <DialogTitle sx={{ fontFamily: "Poppins, sans-serif", pb: 1 }}>
                    Login Required
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily: "Poppins, sans-serif", color: "#4a4a4a" }}>
                        You need to log in first to create the order.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button
                        onClick={() => setLoginModalOpen(false)}
                        sx={{
                            textTransform: "none",
                            borderRadius: "999px",
                            color: "#555",
                            fontFamily: "Poppins, sans-serif",
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disableElevation
                        onClick={() => navigate("/login")}
                        sx={{
                            textTransform: "none",
                            borderRadius: "999px",
                            backgroundColor: "#55C2C3",
                            fontFamily: "Poppins, sans-serif",
                            fontWeight: 600,
                            px: 3,
                            "&:hover": { backgroundColor: "#45b1b2" },
                        }}
                    >
                        Log In
                    </Button>
                </DialogActions>
            </Dialog>

        </div>
    );
};

export default CatalogPage;
